import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, ArrowRight, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryStyle = (cat) => {
  if (cat === 'Circular') return 'bg-[#0F1B2D] text-[#F5F3EE]';
  return 'bg-[#C4A35A] text-[#0F1B2D]';
};

const NoticesSection = () => {
  const [notices, setNotices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/notices?limit=3`)
      .then(res => res.json())
      .then(data => {
        setNotices(data.notices || data);
        setTotalCount(data.total || 0);
      })
      .catch(() => {});
  }, []);

  const handleClick = (notice) => {
    if (notice.file_url) {
      const url = notice.file_url.startsWith('/api')
        ? `${BACKEND_URL}${notice.file_url}`
        : notice.file_url;
      window.open(url, '_blank');
    }
  };

  return (
    <section
      className="section-padding relative"
      data-testid="notices-section"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 border-2 border-[#0F1B2D] flex items-center justify-center">
              <FileText size={18} className="text-[#0F1B2D]" />
            </div>
          </div>
          <span className="section-label mb-4 block">Official Communications</span>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0A1220] tracking-tight mt-4">
            Notices & Circulars
          </h2>
        </div>

        <div className="space-y-0">
          {notices.map((notice, index) => (
            <motion.div
              key={notice.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              viewport={{ once: true }}
              className={`border-b-2 border-[#D8D3CB] first:border-t-2 group ${notice.file_url ? 'cursor-pointer' : ''}`}
              onClick={() => handleClick(notice)}
              data-testid={`notice-item-${index}`}
            >
              <div className="py-5 px-4 sm:px-6 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6">
                {/* Reference & Date */}
                <div className="flex items-center gap-3 sm:flex-col sm:items-start sm:gap-1 sm:min-w-[120px] flex-shrink-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589]">
                    {notice.ref_id}
                  </span>
                  <span className="text-[11px] font-medium text-[#9B9589]">
                    {notice.date}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 ${categoryStyle(notice.category)}`}>
                      {notice.category}
                    </span>
                    <h3 className="text-sm font-semibold text-[#0F1B2D] group-hover:text-[#C4A35A] transition-colors">
                      {notice.title}
                    </h3>
                  </div>
                  <p className="text-[13px] text-[#4A4640] font-medium leading-relaxed">
                    {notice.summary}
                  </p>
                </div>

                {/* Action */}
                <div className="hidden sm:flex items-center flex-shrink-0 pt-1">
                  {notice.file_url ? (
                    <Download size={16} className="text-[#C4A35A] group-hover:text-[#0F1B2D] transition-colors" />
                  ) : (
                    <ArrowRight size={16} className="text-[#D8D3CB] group-hover:text-[#0F1B2D] transition-colors" />
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <div className="border-2 border-[#D8D3CB] p-4 inline-block">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold">
              {totalCount > 3 ? `Showing 3 of ${totalCount} notices — ` : ''}
              Full archive access available to verified members
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NoticesSection;
